import {
  getDatePlaceholder,
  getMarketHasAddress,
  getPersonalNumberPlaceholder,
  personalNumberIsOnlyNumbers,
} from '~/constants/markets';
import React from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '~/hooks/useAppDispatch';
import { RootState } from '~/store';
import useTranslate from '~/hooks/useTranslate';
import { onChangeInputEvent } from '~/types/types';
import useModals from '~/hooks/useModals';
import { pushToGtmLoanAction } from '~/store/actions/gtmActions';
import {
  personAlreadyExistsInList,
  personsAreTheSame,
} from '~/helpers/shared.helper';
import { Button, DatePicker, Stack, TextInput } from '@qred/components-library';
import { v4 as uuidv4 } from 'uuid';
import {
  fullNameValidator,
  personalNumberValidator,
  validateOwnershipPercentage,
  validateYearOfBirth,
} from '~/helpers/validators.helper';
import {
  Controller,
  FormProvider,
  SubmitHandler,
  useForm,
} from 'react-hook-form';
import AMLFields from '~/components/shared/AMLFields/AMLFields';
import OwnershipPercentageSelect from '../OwnershipPercentageSelect';
import { IBeneficialOwner } from '~/interfaces/shared';
import {
  addAdditionalOwner,
  removeAdditionalOwnerById,
  updateAdditionalOwner,
} from '~/store/slices/loanOffer.slice';
import ValidationErrorMessage from '~/components/shared/ValidationErrorMessage/ValidationErrorMessage';

interface OwnerManagementModalProps {
  initialOwner?: IBeneficialOwner;
  isAlternativeBeneficialOwner?: boolean;
  ownershipPercentageAsRanges?: boolean;
  askForPersonalNumber?: boolean;
  askForDateOfBirth?: boolean;
  askForYearOfBirth?: boolean;
}

const OwnerManagementModal = ({
  initialOwner,
  isAlternativeBeneficialOwner,
  ownershipPercentageAsRanges,
  askForPersonalNumber,
  askForDateOfBirth,
  askForYearOfBirth,
}: OwnerManagementModalProps) => {
  const dispatch = useAppDispatch();
  const {
    loanOffer: { form, fetchedBeneficialOwners },
    matchedMedia: { mobile },
    intl: { market, language },
  } = useSelector((state: RootState) => state);
  const translate = useTranslate();

  const formMethods = useForm<IBeneficialOwner>({
    defaultValues: {
      ...(initialOwner || {
        id: uuidv4(),
        isAlternativeBeneficialOwner,
      }),
    },
    mode: 'onTouched',
  });

  const {
    control,
    register,
    handleSubmit,
    getValues,
    setError,
    clearErrors,
    trigger,
    formState: { errors, isSubmitting, isSubmitSuccessful },
  } = formMethods;
  const modals = useModals();

  const isNewOwner = !initialOwner;
  const showAddressFields = getMarketHasAddress(market);

  const trackFieldChange = (event: string | onChangeInputEvent) => {
    let name;
    if (typeof event === 'string') {
      name = event;
    } else {
      name = event.target.name;
    }

    dispatch(
      pushToGtmLoanAction({
        actionName: isNewOwner
          ? `modal_new_owner_${name}_change`
          : `modal_edit_owner_${name}_change`,
      })
    );
  };

  const checkForDuplicates = (formData: IBeneficialOwner) => {
    let error = false;
    if (!isAlternativeBeneficialOwner && !fetchedBeneficialOwners.length) {
      if (personsAreTheSame(formData, form.applicant)) {
        setError('root.SubmitError', {
          type: 'manual',
          message: translate('ValidationErrors.OwnerSameAsApplicant') as string,
        });
        error = true;
      }
    }

    let existingOwners = form.owners;
    if (initialOwner?.id) {
      existingOwners = form.owners.filter((o) => o.id !== initialOwner.id);
    }

    if (personAlreadyExistsInList(existingOwners, formData)) {
      setError('root.SubmitError', {
        type: 'manual',
        message: translate('ValidationErrors.OwnerAlreadyExists') as string,
      });
      error = true;
    }
    return error;
  };

  const saveOwner: SubmitHandler<IBeneficialOwner> = (formData) => {
    const hasSubmissionError = checkForDuplicates(formData);

    if (!hasSubmissionError) {
      dispatch(
        isNewOwner
          ? addAdditionalOwner(formData)
          : updateAdditionalOwner(formData)
      );

      dispatch(
        pushToGtmLoanAction({
          actionName: isNewOwner
            ? 'modal_new_owner_save_pressed'
            : 'modal_edit_owner_save_pressed',
        })
      );
      modals.closeAll();
    }
  };

  const removeOwner = () => {
    dispatch(
      pushToGtmLoanAction({
        actionName: 'modal_loan_offer_delete_owner_pressed',
      })
    );
    const id = getValues('id');
    dispatch(removeAdditionalOwnerById(id));
    modals.closeAll();
  };

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(saveOwner)}>
        <Stack>
          <TextInput
            label={translate('OnboardingOffer.AdditionalOwnerFullName')}
            dataCy="add_new_owner_name"
            placeholder={translate('FullName') as string}
            {...register('fullName', {
              required: translate('ValidationErrors.Required') as string,
              validate: (value) => {
                if (fullNameValidator(value)) {
                  return translate('ValidationErrors.FullName') as string;
                }

                return true;
              },
              onBlur: (e: onChangeInputEvent) => {
                clearErrors('root.SubmitError');
                trackFieldChange(e);
              },
            })}
            error={errors.fullName?.message}
          />
          {askForPersonalNumber && (
            <TextInput
              label={translate('PersonalNumber')}
              dataCy="add_new_owner_personal_number"
              placeholder={getPersonalNumberPlaceholder(market, language)}
              inputMode={
                personalNumberIsOnlyNumbers(market) ? 'numeric' : 'text'
              }
              {...register('personalNumber', {
                required: translate('ValidationErrors.Required') as string,
                validate: (value) => {
                  if (personalNumberValidator(value)) {
                    return translate(
                      'ValidationErrors.PersonalNumber'
                    ) as string;
                  }
                  return true;
                },
                onBlur: (e: onChangeInputEvent) => {
                  clearErrors('root.SubmitError');
                  trackFieldChange(e);
                },
              })}
              error={errors.personalNumber?.message}
            />
          )}

          {askForDateOfBirth && (
            <Controller
              name="dateOfBirth"
              control={control}
              rules={{
                required: translate('ValidationErrors.Required') as string,
              }}
              render={({ field, fieldState }) => (
                <DatePicker
                  label={translate('DateOfBirth')}
                  maxDate={new Date()}
                  placeholder={getDatePlaceholder(market, language)}
                  onChange={(value) => {
                    trackFieldChange(field.name);
                    field.onChange(value);
                    clearErrors('root.SubmitError');
                  }}
                  valueFormat="DD-MM-YYYY"
                  value={field.value}
                  error={fieldState.error?.message}
                />
              )}
            />
          )}

          {askForYearOfBirth && (
            <TextInput
              label={translate('YearOfBirth')}
              inputMode="numeric"
              {...register('yearOfBirth', {
                required: translate('ValidationErrors.Required') as string,
                validate: (value) => {
                  if (value && validateYearOfBirth(value)) {
                    return translate('ValidationErrors.YearOfBirth') as string;
                  }
                  return true;
                },
                onBlur: (e: onChangeInputEvent) => {
                  clearErrors('root.SubmitError');
                  trackFieldChange(e);
                },
              })}
              error={errors.yearOfBirth?.message}
            />
          )}

          {!isAlternativeBeneficialOwner && (
            <>
              {ownershipPercentageAsRanges ? (
                <Controller
                  name="percentage"
                  control={control}
                  rules={{
                    required: translate('ValidationErrors.Required') as string,
                    validate: (value) =>
                      validateOwnershipPercentage(value, translate),
                  }}
                  render={({ field, fieldState }) => (
                    <OwnershipPercentageSelect
                      onChange={(value) => {
                        trackFieldChange(field.name);
                        field.onChange(value?.value || '');
                        trigger('percentage');
                      }}
                      value={field.value}
                      error={fieldState.error?.message}
                    />
                  )}
                />
              ) : (
                <TextInput
                  label={translate('OnboardingOffer.SharesPercentages')}
                  dataCy="add_new_owner_name"
                  rightSection="%"
                  placeholder={
                    translate('OnboardingOffer.moreThan25%') as string
                  }
                  {...register('percentage', {
                    required: translate('ValidationErrors.Required') as string,
                    validate: (value) =>
                      validateOwnershipPercentage(value, translate),
                    onBlur: trackFieldChange,
                  })}
                  error={errors.percentage?.message}
                />
              )}
            </>
          )}

          <AMLFields
            trackFieldChange={trackFieldChange}
            showAddressFields={showAddressFields}
          />

          <Stack spacing="sm" mt="2em">
            {!isNewOwner && (
              <Button
                variant="secondary"
                onClick={removeOwner}
                fullWidth={mobile}
                size="md"
              >
                {translate('OnboardingOffer.DeleteAdditionalOwner')}
              </Button>
            )}

            <Stack>
              {errors.root?.SubmitError && (
                <ValidationErrorMessage>
                  {errors.root?.SubmitError?.message}
                </ValidationErrorMessage>
              )}

              <Button
                variant="primary"
                dataCy="add_new_owner_modal_button"
                fullWidth={mobile}
                size="md"
                type="submit"
                disabled={isSubmitting || isSubmitSuccessful}
              >
                {translate('Save')}
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </form>
    </FormProvider>
  );
};

export default OwnerManagementModal;
